/**
 * Registry-aware row key — generic-tagged for variant narrowing.
 *
 * Wraps a canonical `RowId` and can self-resolve to a `RowLocalId`
 * via a registry. Delegates all parsing + canonicalization to
 * `RowId`; `RowKey` exists as a distinct type so call sites that
 * need "identity you can pass across APIs" can accept something
 * stronger than a raw string without committing to a session-
 * scoped `RowLocalId`.
 *
 * The pre-port tento/tento-lona-js/sheets class exposed `rowId` as a public
 * field; this revision keeps the `rowId` accessor (now a getter
 * proxying to the private `wrappedId` field) so existing rows-side
 * call sites continue to work.
 */

import type { Option } from "../core/option.ts";
import { nonnull } from "@tento-core/assert";

import { RowId } from "./row-id.ts";
import type { RowLocalId } from "./row-local-id.ts";

/**
 * A row identity to pass across APIs: it wraps a canonical `RowId` and
 * resolves to a `RowLocalId` through a registry.
 */
export class RowKey<V extends RowKey.VariantTag = RowKey.VariantTag> {
  declare readonly __rowKeyVariant: V;

  readonly #wrappedId: RowId;

  private constructor(id: RowId) {
    this.#wrappedId = id;
  }

  // ── Factory methods (delegate to RowId) ────────────────────────

  static of(raw: string): RowKey {
    return new RowKey(RowId.of(raw));
  }

  /** Wrap an existing `RowId` directly. */
  static fromRowId(id: RowId): RowKey {
    return new RowKey(id);
  }

  static user(hex: string): RowKey.User {
    return new RowKey(RowId.fromUuid(hex)) as RowKey.User;
  }

  static lookup(key: string): RowKey.Lookup {
    return new RowKey(RowId.lookup(key)) as RowKey.Lookup;
  }

  static reserved(key: string): RowKey.Reserved {
    return new RowKey(RowId.reserved(key)) as RowKey.Reserved;
  }

  /**
   * A row's lookup key as the wire writes it (`:~lona:tasks`, `:revenue`,
   * `[owner]:key`) — what a verb that addresses a keyed row takes
   * (`rows.linkRow`). Throws when `raw` names a row by id (`r_…`, a UUID)
   * or a local form (`system(…)`, `pending(…)`).
   */
  static keyed(raw: string): RowKey.Keyed {
    const key = RowKey.of(raw);
    if (key.isLookup() || key.isReserved()) return key;
    throw new Error(`RowKey.keyed: ${raw} is not a lookup key`);
  }

  static virtual(parentHex: string, preset: string): RowKey.Virtual {
    const hex = parentHex.toLowerCase();
    return new RowKey(
      RowId.of(`virtual(r_${hex}|${preset})`),
    ) as RowKey.Virtual;
  }

  static system(name: string, param: Option<string>): RowKey.System {
    return new RowKey(RowId.system(name, param)) as RowKey.System;
  }

  static pending(uuid: Option<string>): RowKey.Pending {
    return new RowKey(RowId.pending(uuid)) as RowKey.Pending;
  }

  // ── Self-resolve ───────────────────────────────────────────────

  /**
   * Resolve this key to a `RowLocalId` in `registry` — YOUR identity scope
   * (the client's `platformOf(client).ids`, a sheet's `registry`, or a
   * test's own `new RowLocalId.Registry()`). There is no process-global
   * default: two clients never share identity by omission.
   */
  resolve(registry: { resolve(key: RowKey): RowLocalId }): RowLocalId {
    return registry.resolve(this);
  }

  // ── Variant guards (delegate to underlying RowId) ──────────────

  isUser():     this is RowKey.User { return this.#wrappedId.isUser(); }
  isLookup():   this is RowKey.Lookup { return this.#wrappedId.isLookup(); }
  isReserved(): this is RowKey.Reserved { return this.#wrappedId.isReserved(); }
  isVirtual():  this is RowKey.Virtual { return this.#wrappedId.isVirtual(); }
  isSystem():   this is RowKey.System { return this.#wrappedId.isSystem(); }
  isTemplate(): this is RowKey.Template { return this.#wrappedId.isTemplate(); }
  isPending():  this is RowKey.Pending { return this.#wrappedId.isPending(); }

  // ── Extractors ─────────────────────────────────────────────────

  asLookup(): Option<string> { return this.#wrappedId.asLookup(); }

  asVirtual(): Option<RowKey.VirtualInfo> {
    const v = this.#wrappedId.asVirtual();
    if (!v) return null;
    // `RowId.virtual` refuses a non-user parent, so a virtual id's parent has hex.
    const parentHex = nonnull(
      v.parentId.toHex(),
      `RowKey.asVirtual: virtual parent ${v.parentId} is not a user row`,
    );
    return { parentHex, preset: v.preset };
  }

  asSystem(): Option<RowKey.SystemInfo> { return this.#wrappedId.asSystem(); }
  toHex():    Option<string>            { return this.#wrappedId.toHex(); }
  toUuid():   Option<string>            { return this.#wrappedId.toUuid(); }

  /** Access the underlying `RowId`. Pre-port API kept as a getter. */
  get rowId(): RowId { return this.#wrappedId; }

  /** Alias of `rowId` — matches lona-js's accessor name. */
  get id(): RowId { return this.#wrappedId; }

  // ── Interop ────────────────────────────────────────────────────

  toString(): string { return this.#wrappedId.toString(); }
  toJSON(): string { return this.#wrappedId.toJSON(); }
}

/**
 * Types that go with `RowKey`: `VariantTag`, `User`, `Lookup`, `Reserved`,
 * `Virtual`, `System`, and 6 more.
 */
export namespace RowKey {
  export type VariantTag =
    | "user"
    | "lookup"
    | "reserved"
    | "virtual"
    | "system"
    | "template"
    | "pending";

  export type User = RowKey<"user">;
  export type Lookup = RowKey<"lookup">;
  export type Reserved = RowKey<"reserved">;
  export type Virtual = RowKey<"virtual">;
  export type System = RowKey<"system">;
  export type Template = RowKey<"template">;
  export type Pending = RowKey<"pending">;
  /** A row named by its lookup key (`RowKey.keyed`): a lookup or reserved key. */
  export type Keyed = Lookup | Reserved;

  export type VirtualInfo = { parentHex: string; preset: string };
  export type SystemInfo = { name: string; param: Option<string> };

  export type Variant =
    | { tag: "user"; hex: string }
    | { tag: "lookup"; key: string }
    | { tag: "reserved"; key: string }
    | { tag: "virtual"; parentHex: string; preset: string }
    | { tag: "system"; name: string; param: Option<string> };
}
